// Session RC1: the subcontractor TYPE choice, shared by the Add subcontractor modal and the "Type not set" prompt on a
// subcontractor's page. Two large cards side by side (one column on a phone), then for a Labour subcontractor the card
// source (Standard card, the default; Custom rates for an admin only, because rates are admin only), the Technician tick
// and the effective date (today by default, never in the past). Controlled: the parent holds the value.
import type { RateCardSource, SubcontractorKind } from "../../lib/api";

export const KIND_CARDS: { kind: SubcontractorKind; title: string; line: string }[] = [
  { kind: "LABOUR", title: "Labour subcontractor", line: "Engineers who work for us, paid from a rate card." },
  { kind: "SUPPLY_AND_FIT", title: "Supply and fit company", line: "A firm that prices and supplies its own work, paid on purchase orders." },
];

export const SUPPLY_AND_FIT_NOTE = "Paid on purchase orders against their own quotes.";
export const TECHNICIAN_LINE = "Engineers linked to this firm are marked as technicians.";

export interface TypeChoiceValue {
  kind: SubcontractorKind | null;
  rateCardSource: RateCardSource;
  technician: boolean;
  effectiveFrom: string;
}

export function KindCards({ value, onChange, disabled }: { value: SubcontractorKind | null; onChange: (k: SubcontractorKind) => void; disabled?: boolean }) {
  return (
    <div role="radiogroup" aria-label="Type of subcontractor" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {KIND_CARDS.map((c) => {
        const on = value === c.kind;
        return (
          <button
            key={c.kind}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            onClick={() => onChange(c.kind)}
            className={`min-h-24 rounded-ele border p-4 text-left transition disabled:opacity-60 ${
              on ? "border-ele-green bg-ele-inset" : "border-ele-purple-accent bg-ele-surface hover:border-ele-green"
            }`}
          >
            <span className={`block font-heading text-lg tracking-wide ${on ? "text-ele-green" : "text-ele-strong"}`}>{c.title}</span>
            <span className="mt-1 block text-sm text-ele-body">{c.line}</span>
          </button>
        );
      })}
    </div>
  );
}

const LABEL = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue";
const FIELD = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong";

/** The Labour options under the cards: card source, Technician tick, effective date. */
export function LabourOptions({
  value,
  onChange,
  isAdmin,
  today,
  disabled,
}: {
  value: TypeChoiceValue;
  onChange: (next: TypeChoiceValue) => void;
  isAdmin: boolean;
  today: string;
  disabled?: boolean;
}) {
  return (
    <div className="mt-4 space-y-3">
      <div role="radiogroup" aria-label="Rates" className="flex flex-wrap gap-2">
        {(["STANDARD", "CUSTOM"] as const)
          .filter((src) => src === "STANDARD" || isAdmin)
          .map((src) => {
            const on = value.rateCardSource === src;
            return (
              <button
                key={src}
                type="button"
                role="radio"
                aria-checked={on}
                disabled={disabled}
                onClick={() => onChange({ ...value, rateCardSource: src })}
                className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
                  on ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
                }`}
              >
                {src === "STANDARD" ? "Standard card" : "Custom rates"}
              </button>
            );
          })}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className={LABEL}>Rates start on</span>
          <input
            type="date"
            aria-label="Rates start on"
            min={today}
            value={value.effectiveFrom}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, effectiveFrom: e.target.value })}
            className={FIELD}
          />
        </label>
        <TechnicianTick value={value} onChange={onChange} disabled={disabled} />
      </div>
    </div>
  );
}

export function TechnicianTick({ value, onChange, disabled }: { value: TypeChoiceValue; onChange: (next: TypeChoiceValue) => void; disabled?: boolean }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-sm text-ele-strong max-sm:min-h-11">
        <input type="checkbox" checked={value.technician} disabled={disabled} onChange={(e) => onChange({ ...value, technician: e.target.checked })} />
        Technician
      </label>
      <p className="mt-1 text-xs text-ele-body">{TECHNICIAN_LINE}</p>
    </div>
  );
}
